import * as React from 'react';
import BPromise from 'bluebird';
import { History, Location } from 'history';
import { parse } from 'query-string';
import { Grid2 } from 'Src/components/Grid/Grid2';
import { Loader } from 'Src/components/Loader/Loader';
import { ActionItem } from 'Src/components/ActionsButton/ActionsButton';
import { FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import {
  PAYMENT_ACCOUNT_STATUS_SERVER_TO_CLIENT_MAPPING,
  PAYMENT_TYPE_SERVER_TO_CLIENT_MAPPING
} from 'Src/consts/paymentAccount';
import { PaymentAccount } from 'Src/models/paymentAccount';
import { getPaymentAccountState, getPaymentAccountStatus } from 'Src/helpers/accessor/accessor';
import { getSelectOptionForBooleanQuestion } from 'Src/helpers/table/select';
import {
  ColumnDefinition,
  getFilters2,
  getOrder,
  getSearchFilter,
  getSearchOrder,
  getServerFieldSectionWhere,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import {
  getAllSuperAdminSchoolPaymentAccounts,
  getSuperAdminSchoolPaymentAccounts,
  getSuperAdminSchoolPaymentAccountsCount
} from 'Src/helpers/service/superadmin/payments';

function getPaymentType(account: PaymentAccount): string {
  const type = account.type;

  return typeof type !== 'undefined' ? PAYMENT_TYPE_SERVER_TO_CLIENT_MAPPING[type.toUpperCase()] || type : '';
}

function getStripeEmail(account: PaymentAccount): string {
  return account.stripeData && account.stripeData.email ? account.stripeData.email : '';
}

function getStripeCountry(account: PaymentAccount): string {
  return account.stripeData && account.stripeData.country ? account.stripeData.country : '';
}

function getStripeCurrency(account: any): string {
  const currency = account.stripeData && account.stripeData.defaultCurrency;

  return typeof currency === 'string' ? currency.toUpperCase() : '';
}

function getVerificationFields(account: any): string {
  const fields = account.stripeData && account.stripeData.verification && account.stripeData.verification.fieldsNeeded;

  return Array.isArray(fields) && fields.length > 0 ? fields.join(', ') : '';
}

function getSelectOptionsFromMapping(mapping: any) {
  return Object.keys(mapping).map(key => ({
    text: mapping[key],
    value: key
  }));
}

function getPaymentTypeOptions() {
  return Object.keys(PAYMENT_TYPE_SERVER_TO_CLIENT_MAPPING).map(key => ({
    text: PAYMENT_TYPE_SERVER_TO_CLIENT_MAPPING[key],
    value: key.toLowerCase()
  }));
}

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Name',
    field: 'name',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['name']
  },
  {
    text: 'Status',
    field: 'status',
    isSort: true,
    type: FILTER_TYPE.SELECT,
    accessor: getPaymentAccountStatus
  },
  {
    text: 'Type',
    field: 'type',
    isSort: true,
    type: FILTER_TYPE.SELECT,
    accessor: getPaymentType
  },
  {
    text: 'Default',
    field: 'isDefault',
    isSort: true,
    type: FILTER_TYPE.SELECT,
    accessor: getPaymentAccountState
  },
  {
    text: 'Stripe account',
    field: 'accountId',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['accountId']
  },
  {
    text: 'Email',
    field: 'stripeEmail',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getStripeEmail
  },
  {
    text: 'Country',
    field: 'stripeCountry',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getStripeCountry
  },
  {
    text: 'Currency',
    field: 'stripeCurrency',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getStripeCurrency
  },
  {
    text: 'Verification needed',
    field: 'verification',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getVerificationFields
  }
];

interface Props {
  schoolId: string;
  history: History;
  location: Location;
}

interface State {
  items: PaymentAccount[];
  filterOptions: any;
  currentPage: number;
  selectedItems: PaymentAccount[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  filters: any;
  isDataFiltered: boolean;
  isLoading: boolean;
  total: number;
  basePath: string;
  isSelectAllChecked: boolean;
  errorMessage: string;
}

export class SuperAdminSchoolPayments extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      items: [],
      filterOptions: this.getFilterOptions(),
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      filters: {},
      isDataFiltered: false,
      isLoading: false,
      total: 0,
      basePath: '',
      isSelectAllChecked: false,
      errorMessage: ''
    };
  }

  componentDidMount() {
    this.setState({ isLoading: true, errorMessage: '' });
    this.setItems();
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.location.search !== this.props.location.search || nextProps.schoolId !== this.props.schoolId) {
      this.setState({ isLoading: true, errorMessage: '' });
      this.setItems(nextProps);
    }
  }

  getFilterOptions() {
    return {
      status: getSelectOptionsFromMapping(PAYMENT_ACCOUNT_STATUS_SERVER_TO_CLIENT_MAPPING),
      type: getPaymentTypeOptions(),
      isDefault: getSelectOptionForBooleanQuestion()
    };
  }

  setItems(props: Props = this.props) {
    const { history, schoolId } = props;
    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':') as [string, TABLE_SORT_DIRECTION];
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';
    const filters = getFilters2(COLUMNS, search);
    const isShowFilter = isFilterExist2(filters);
    const isDataFiltered = isFilterExist2(filters);
    const where = getServerFieldSectionWhere(filters);
    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : 'name ASC';
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    return BPromise.all([
      getSuperAdminSchoolPaymentAccountsCount(schoolId, where),
      getSuperAdminSchoolPaymentAccounts(schoolId, serverQueryFilter)
    ])
      .then(([countObj, items]) => {
        this.setState({
          currentPage: page,
          sortDirection,
          sortColumnsName,
          isShowFilter,
          isDataFiltered,
          filters,
          items,
          total: countObj.count,
          basePath: history.location.pathname,
          isLoading: false,
          errorMessage: ''
        });

        return true;
      })
      .catch(error => {
        console.error('Can not load superadmin school payment accounts', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not load payment accounts. Please check the session and try again.'
        });
      });
  }

  setCurrentPageParams = (currentPage: number): void => {
    const search: string[] = [];
    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== FIRST_PAGE) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];
    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    const selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({ selectedItems: selectedItemsUpdated });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;
    const itemsFiltered = items.filter(item => selectedItems.every(selectedItem => selectedItem.id !== item.id));

    this.setState({
      selectedItems: [...selectedItems, ...itemsFiltered],
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;
    const where = getServerFieldSectionWhere(filters);

    this.setState({ isLoading: true, errorMessage: '' });

    getAllSuperAdminSchoolPaymentAccounts(this.props.schoolId, where)
      .then(items => {
        const itemsFiltered = items.filter(item => selectedItems.every(selectedItem => selectedItem.id !== item.id));

        this.setState({
          selectedItems: [...selectedItems, ...itemsFiltered],
          isSelectAllChecked: true,
          isLoading: false
        });
      })
      .catch(error => {
        console.error('Can not select all superadmin school payment accounts', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not select all payment accounts. Please try again.'
        });
      });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;
    const order = getOrder(sortField, sortDirection, sortColumnsName);
    const search: string[] = [`order=${sortField}:${order}`];

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (event, filterField: string): void => {
    const filterValue = event.target.value;

    this.setState({
      filters: {
        ...this.state.filters,
        [filterField]: filterValue
      }
    });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    const search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onClearFilterClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: ''
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    this.setState({
      isShowFilter: !this.state.isShowFilter
    });
  };

  render() {
    const {
      currentPage,
      errorMessage,
      filters,
      filterOptions,
      isDataFiltered,
      isLoading,
      isSelectAllChecked,
      isShowFilter,
      items,
      selectedItems,
      sortColumnsName,
      sortDirection,
      total
    } = this.state;

    const actionItems: ActionItem[] = [
      {
        itemText: 'View payment account',
        isActive: false,
        onItemClick: () => undefined
      }
    ];

    if (isLoading) {
      return <Loader />;
    }

    return (
      <div className="SuperAdminSchoolPayments">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="mb-1">Payment accounts</h4>
            <div className="text-muted">Read-only Stripe payment account list migrated onto the shared Grid2.</div>
          </div>
          <button className="btn btn-outline-secondary" onClick={() => this.setItems()}>
            Refresh
          </button>
        </div>

        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}

        <Grid2
          dataItems={items}
          filters={filters}
          currentPage={currentPage}
          total={total}
          isSelectAllChecked={isSelectAllChecked}
          selectedItemsCount={selectedItems.length}
          isDataFiltered={isDataFiltered}
          sortDirection={sortDirection}
          sortColumnsName={sortColumnsName}
          isShowFilter={isShowFilter}
          dataItemsSelected={selectedItems}
          columns={COLUMNS}
          actionItems={actionItems}
          options={filterOptions}
          onItemClick={this.onItemClick}
          onSortClick={this.onTableSortClick}
          onApplyFilterClick={this.onApplyFilterClick}
          onClearFilterClick={this.onClearFilterClick}
          onTableFilterChange={this.onTableFilterChange}
          onTableFilterClick={this.onTableFilterClick}
          setCurrentPageParams={this.setCurrentPageParams}
          onSelectAllClick={this.onSelectAllClick}
          onSelectAllOnPageClick={this.onSelectAllOnPageClick}
          onUnselectAllClick={this.onUnselectAllClick}
          isCSV={true}
        />
      </div>
    );
  }
}

